import {
  model,
  Model,
  prop,
  modelAction,
  registerRootStore
} from "mobx-keystone"
import { computed } from "mobx"
import { createContext } from "react"
import {
  BOOTSTRAP_SCRIPT_ID,
  PAGE_TITLES,
  SITE_PAGES,
  type SitePage
} from "../helpers/constants"
import { buildPath, parseLocation, parsePathname } from "../helpers/routing"
import type { ApiPlatform } from "../../docs/types"
import { frameworkById, frameworkTitle, platformById, platformHeadline, quickstartTitle } from "../../docs/catalog"
import { processorById, processorHeadline } from "../../processors/catalog"
import { DocsBrowser } from "./DocsBrowser"
import { GuidesBrowser } from "./GuidesBrowser"
import { ShopStore } from "./shop/ShopStore"
import type { ShopBootstrap } from "../shop/types"

/* Intrinsic size travels with the URL: the two hero sets have different aspect
   ratios, and <picture> reserves its box from these numbers. */
/* srcset, when present, is the full "url 720w, url 1448w" string: every width
   the server ships of this crop, the master included. See
   AppController::HERO_ART_VARIANTS. */
type HeroArtCrop = { url?: string; width?: number; height?: number; srcset?: string }

// One /learn article (LearnPage.cards on the server). footer_label is null for
// an article the footer leaves out.
export type LearnCard = {
  path: string
  title: string
  description: string
  eyebrow: string
  footer_label: string | null
  lang: string
  // On the homepage's LEARN grid (LearnPage#home?).
  home: boolean
}

type AppBootstrap = {
  public_origin?: string
  logo_url?: string
  mark_url?: string
  wordmark_url?: string
  icon_urls?: Record<string, string>
  framework_logos?: Record<string, string>
  platform_logos?: Record<string, string>
  landing_images?: Record<string, string>
  learn_pages?: LearnCard[]
  hero_art?: {
    desktop?: HeroArtCrop
    mobile?: HeroArtCrop
    /* Which set is in play. The two sets need different edge treatment, so this
       reaches the DOM as a data attribute rather than being inferred from the
       filename. See AppController::HERO_ART_VARIANTS. */
    variant?: string
  }
  shop?: ShopBootstrap
}

@model("or/AppWorkspace")
export class AppWorkspace extends Model({
  hydrated: prop<boolean>(false),
  openPage: prop<SitePage>(SITE_PAGES.HOME),
  publicOrigin: prop<string>("https://openreceive.org"),
  logoUrl: prop<string>(""),
  markUrl: prop<string>(""),
  wordmarkUrl: prop<string>(""),
  iconUrls: prop<Record<string, string>>(() => ({})),
  // frameworks[].id → the logo's digested asset URL (AppController::FRAMEWORK_LOGOS).
  frameworkLogos: prop<Record<string, string>>(() => ({})),
  // platforms[].id → the logo's digested asset URL (AppController::PLATFORM_LOGOS).
  platformLogos: prop<Record<string, string>>(() => ({})),
  // config/landing_copy.json image file → its digested asset URL.
  landingImages: prop<Record<string, string>>(() => ({})),
  // The /learn articles, in the server's order.
  learnPages: prop<LearnCard[]>(() => []),
  heroArtDesktop: prop<HeroArtCrop | null>(null),
  heroArtMobile: prop<HeroArtCrop | null>(null),
  heroArtVariant: prop<string>(""),
  hash: prop<string>(""),
  // /integrations/<id> — the framework page that is open, by the contract's
  // id. An id the bundle does not carry still parses (the page says so).
  frameworkId: prop<string | null>(null),
  // /platforms/<id> — the platform page that is open; null on the index.
  platformId: prop<string | null>(null),
  // /processors/<id> — the processor page that is open; null on the index.
  processorId: prop<string | null>(null),
  docs: prop<DocsBrowser>(() => new DocsBrowser({})),
  guides: prop<GuidesBrowser>(() => new GuidesBrowser({})),
  shop: prop<ShopStore>(() => new ShopStore({}))
}) {
  @computed
  get currentPath() {
    return buildPath({
      page: this.openPage,
      guideSlug: this.guides.slug,
      frameworkId: this.frameworkId,
      platformId: this.platformId,
      processorId: this.processorId,
      apiPlatform: this.docs.platform,
      docsQuery: this.openPage === SITE_PAGES.API_DOCS ? this.docs.query : this.guides.query,
      hash: this.hash
    })
  }

  // A guide's title is in the guides chunk (docs/lazy). Until it lands the
  // server-rendered <title> stays, and the pageview waits for the real one.
  @computed
  get titlePending() {
    return this.openPage === SITE_PAGES.GUIDES && this.guides.slug != null && this.guides.loading
  }

  @computed
  get pageTitle() {
    if (this.openPage === SITE_PAGES.GUIDES && this.guides.current) {
      return quickstartTitle(this.guides.current.path) ?? `${this.guides.current.title} — OpenReceive`
    }
    if (this.openPage === SITE_PAGES.INTEGRATION && this.framework) {
      return frameworkTitle(this.framework)
    }
    if (this.openPage === SITE_PAGES.PLATFORMS && this.platform) {
      return `${platformHeadline(this.platform)} — OpenReceive`
    }
    if (this.openPage === SITE_PAGES.PROCESSORS && this.processor) {
      return `${processorHeadline(this.processor)} — OpenReceive`
    }
    return PAGE_TITLES[this.openPage]
  }

  @computed
  get framework() {
    return frameworkById(this.frameworkId)
  }

  @computed
  get platform() {
    return platformById(this.platformId)
  }

  @computed
  get processor() {
    return processorById(this.processorId)
  }

  iconUrl(name: string): string {
    return this.iconUrls[name] ?? ""
  }

  frameworkLogoUrl(id: string): string {
    return this.frameworkLogos[id] ?? ""
  }

  platformLogoUrl(id: string): string {
    return this.platformLogos[id] ?? ""
  }

  landingImageUrl(file: string): string {
    return this.landingImages[file] ?? ""
  }

  absoluteUrl(pathAndHash: string): string {
    const origin = this.publicOrigin.replace(/\/$/, "")
    if (pathAndHash.startsWith("http")) return pathAndHash
    if (pathAndHash.startsWith("#")) {
      return `${origin}${window.location.pathname}${window.location.search}${pathAndHash}`
    }
    return `${origin}${pathAndHash.startsWith("/") ? pathAndHash : `/${pathAndHash}`}`
  }

  @modelAction
  applyLocation = (url: URL = new URL(window.location.href)) => {
    const parsed = parseLocation(url)
    this.openPage = parsed.page
    this.hash = parsed.hash
    this.frameworkId = parsed.frameworkId
    this.platformId = parsed.platformId
    this.processorId = parsed.processorId
    this.guides.setSlug(parsed.guideSlug)
    if (parsed.page === SITE_PAGES.API_DOCS) {
      this.docs.setPlatform(parsed.apiPlatform)
      this.docs.setQuery(parsed.docsQuery)
      this.docs.selectAnchor(parsed.hash || null)
    } else if (parsed.page === SITE_PAGES.GUIDES) {
      this.guides.setQuery(parsed.docsQuery)
    }
  }

  @modelAction
  hydrateDataFromPage = () => {
    const script = document.getElementById(BOOTSTRAP_SCRIPT_ID)
    if (script?.textContent) {
      try {
        const data = JSON.parse(script.textContent) as AppBootstrap
        if (data.public_origin) this.publicOrigin = data.public_origin.replace(/\/$/, "")
        if (data.logo_url) this.logoUrl = data.logo_url
        if (data.mark_url) this.markUrl = data.mark_url
        if (data.wordmark_url) this.wordmarkUrl = data.wordmark_url
        if (data.icon_urls) this.iconUrls = { ...data.icon_urls }
        if (data.framework_logos) this.frameworkLogos = { ...data.framework_logos }
        if (data.platform_logos) this.platformLogos = { ...data.platform_logos }
        if (data.landing_images) this.landingImages = { ...data.landing_images }
        if (data.learn_pages) this.learnPages = data.learn_pages.map((page) => ({ ...page }))
        if (data.hero_art?.desktop?.url) this.heroArtDesktop = { ...data.hero_art.desktop }
        if (data.hero_art?.mobile?.url) this.heroArtMobile = { ...data.hero_art.mobile }
        if (data.hero_art?.variant) this.heroArtVariant = data.hero_art.variant
        if (data.shop) this.shop.hydrate(data.shop)
      } catch (err) {
        console.error(err, { tags: { where: "hydrateDataFromPage" } })
      }
    }
    this.applyLocation()
    this.hydrated = true
  }

  @modelAction
  syncFromLocation = () => {
    this.applyLocation()
  }

  @modelAction
  private writeUrl = (replace = false) => {
    const next = this.currentPath
    const method = replace ? "replaceState" : "pushState"
    window.history[method]({}, "", next)
  }

  @modelAction
  goHome = (hash = "") => {
    this.openPage = SITE_PAGES.HOME
    this.guides.setSlug(null)
    this.frameworkId = null
    this.platformId = null
    this.processorId = null
    this.hash = hash
    this.writeUrl()
  }

  // A framework landing page. `hash` scrolls to a section of it ("demo").
  @modelAction
  goIntegration = (id: string, hash = "") => {
    this.openPage = SITE_PAGES.INTEGRATION
    this.guides.setSlug(null)
    this.frameworkId = id
    this.platformId = null
    this.processorId = null
    this.hash = hash
    this.writeUrl()
  }

  // /platforms, or one platform's page when `id` is given.
  @modelAction
  goPlatforms = (id: string | null = null) => {
    this.openPage = SITE_PAGES.PLATFORMS
    this.guides.setSlug(null)
    this.frameworkId = null
    this.platformId = id
    this.processorId = null
    this.hash = ""
    this.writeUrl()
  }

  // /processors, or one processor's page when `id` is given.
  @modelAction
  goProcessors = (id: string | null = null) => {
    this.openPage = SITE_PAGES.PROCESSORS
    this.guides.setSlug(null)
    this.frameworkId = null
    this.platformId = null
    this.processorId = id
    this.hash = ""
    this.writeUrl()
  }

  @modelAction
  goApiDocs = (opts?: { platform?: "all" | ApiPlatform; query?: string; hash?: string }) => {
    this.openPage = SITE_PAGES.API_DOCS
    this.guides.setSlug(null)
    if (opts?.platform) this.docs.setPlatform(opts.platform)
    if (opts?.query != null) this.docs.setQuery(opts.query)
    this.hash = opts?.hash ?? ""
    this.docs.selectAnchor(this.hash || null)
    this.writeUrl()
  }

  @modelAction
  goGuides = (slug: string | null = null) => {
    this.openPage = SITE_PAGES.GUIDES
    this.guides.setSlug(slug)
    if (slug) this.guides.setQuery("")
    this.hash = ""
    this.writeUrl()
  }

  @modelAction
  setDocsQuery = (query: string) => {
    this.docs.setQuery(query)
    this.writeUrl(true)
  }

  @modelAction
  setDocsPlatform = (platform: "all" | ApiPlatform) => {
    this.docs.setPlatform(platform)
    this.writeUrl(true)
  }

  @modelAction
  setGuidesQuery = (query: string) => {
    this.guides.setQuery(query)
    this.writeUrl(true)
  }

  @modelAction
  setHash = (hash: string, replace = false) => {
    this.hash = hash.replace(/^#/, "")
    if (this.openPage === SITE_PAGES.API_DOCS) this.docs.selectAnchor(this.hash || null)
    this.writeUrl(replace)
  }

  @modelAction
  openHref = (href: string, replace = false) => {
    if (!href) return
    if (/^https?:/i.test(href) && !href.startsWith(this.publicOrigin)) {
      window.open(href, "_blank", "noopener,noreferrer")
      return
    }
    const url = new URL(href, this.publicOrigin)
    const { page } = parsePathname(url.pathname)
    // A path the SPA does not draw (/contact, the NWC page, a payload .md) is
    // a full navigation; parsePathname answers HOME for those only because it
    // has no better answer.
    if (page === SITE_PAGES.HOME && url.pathname !== "/") {
      window.location.assign(url.pathname)
      return
    }
    this.applyLocation(url)
    this.writeUrl(replace)
  }
}

export const runCreateAppWorkspace = (): AppWorkspace => {
  const workspace = new AppWorkspace({})
  registerRootStore(workspace)
  return workspace
}

const createdWorkspace = runCreateAppWorkspace()
export { createdWorkspace }
export const AppWorkspaceContext = createContext<AppWorkspace>(createdWorkspace)
